/**
 * Travel Details Page - Phase 2F
 * Patient form for entering travel information
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTravel } from '../../context/TravelContext';
import { usePatientFlow } from '../../context/PatientFlowContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
// TREATX-UAT-PHONE-BULK-MIGRATION-02 — canonical E.164 phone input.
import { PhoneInput } from '../../components/ui/phone-input';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, Plane, Hotel, Car, Calendar, Clock,
  CheckCircle, AlertCircle, Save, User, Phone, MapPin
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import DateField from '../../components/DateField';

const TravelDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { flowState, updateFlowState } = usePatientFlow();
  const { getTravelDetails, saveTravelDetails, getTravelStatus } = useTravel();
  const { user } = useAuth();
  
  // Patient & clinic IDs from auth context
  const patientId = user?.id || user?.patient_id || '';
  const clinicId = flowState.clinicsSelected?.[0] || '';
  
  const existingDetails = getTravelDetails(patientId);
  const status = getTravelStatus(patientId);
  
  const [formData, setFormData] = useState({
    departureCountry: '',
    departureCity: '',
    arrivalAirport: '',
    arrivalFlightNumber: '',
    arrivalDate: '',
    arrivalTime: '',
    departureAirport: '',
    departureFlightNumber: '',
    departureDate: '',
    departureTime: '',
    needsAirportPickup: false,
    needsAirportDropoff: false,
    hotelName: '',
    hotelAddress: '',
    hotelCheckIn: '',
    hotelCheckOut: '',
    specialRequirements: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    }
  });
  
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (existingDetails) {
      setFormData({
        departureCountry: existingDetails.departureCountry || '',
        departureCity: existingDetails.departureCity || '',
        arrivalAirport: existingDetails.arrivalAirport || '',
        arrivalFlightNumber: existingDetails.arrivalFlightNumber || '',
        arrivalDate: existingDetails.arrivalDate || '',
        arrivalTime: existingDetails.arrivalTime || '',
        departureAirport: existingDetails.departureAirport || '',
        departureFlightNumber: existingDetails.departureFlightNumber || '',
        departureDate: existingDetails.departureDate || '',
        departureTime: existingDetails.departureTime || '',
        needsAirportPickup: existingDetails.needsAirportPickup || false,
        needsAirportDropoff: existingDetails.needsAirportDropoff || false,
        hotelName: existingDetails.hotelName || '',
        hotelAddress: existingDetails.hotelAddress || '',
        hotelCheckIn: existingDetails.hotelCheckIn || '',
        hotelCheckOut: existingDetails.hotelCheckOut || '',
        specialRequirements: existingDetails.specialRequirements || '',
        emergencyContact: existingDetails.emergencyContact || {
          name: '',
          phone: '',
          relationship: ''
        }
      });
    }
  }, [existingDetails]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmergencyContactChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      saveTravelDetails(patientId, clinicId, formData);
      
      // Priority 1 - Item 3: Update journey tracker to mark Travel as completed
      updateFlowState({
        travelDetailsCompleted: true,
        currentStep: 'travel-complete'
      });
      
      toast({
        title: '✅ Travel Details Saved',
        description: 'Your travel information has been updated. Redirecting to dashboard...'
      });
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/patient/dashboard');
      }, 1500);
      
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save travel details. Please try again.',
        variant: 'destructive'
      });
      setIsSaving(false);
    }
  };

  const getStatusBadge = () => {
    switch (status) {
      case 'confirmed':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            {t('patient.travel.completeStatus')}
          </Badge>
        );
      case 'partial':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            {t('patient.journey.stepBooked')}
          </Badge>
        );
      default:
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            {t('patient.travel.incompleteStatus')}
          </Badge>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Toaster />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-600 to-blue-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('patient.common.back')}
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Plane className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">{t('patient.travel.title')}</h1>
                <p className="text-sky-100 mt-1">
                  {t('patient.travel.pageIntro')}
                </p>
              </div>
            </div>
            {getStatusBadge()}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Arrival Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="h-5 w-5 text-sky-600" />
              {t('patient.travel.arrivalInfo')}
            </CardTitle>
            <CardDescription>{t('patient.travel.arrivalInfoSub')}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <Label>{t('patient.travel.departureCountry')}</Label>
                <Input
                  value={formData.departureCountry}
                  onChange={(e) => handleChange('departureCountry', e.target.value)}
                  placeholder={t('patient.travel.countryPlaceholder')}
                />
              </div>
              <div>
                <Label>{t('patient.travel.departureCity')}</Label>
                <Input
                  value={formData.departureCity}
                  onChange={(e) => handleChange('departureCity', e.target.value)}
                  placeholder={t('patient.travel.cityPlaceholder')}
                />
              </div>
            </div>
            
            <div>
              <Label>{t('patient.travel.arrivalAirport')} {t('patient.travel.requiredMark')}</Label>
              <Input
                value={formData.arrivalAirport}
                onChange={(e) => handleChange('arrivalAirport', e.target.value)}
                placeholder={t('patient.travel.airportPlaceholder')}
                required
              />
            </div>
            
            <div>
              <Label>{t('patient.travel.flightNumberRequired')} {t('patient.travel.requiredMark')}</Label>
              <Input
                value={formData.arrivalFlightNumber}
                onChange={(e) => handleChange('arrivalFlightNumber', e.target.value)}
                placeholder={t('patient.travel.flightPlaceholder')}
                required
              />
            </div>
            
            <div>
              <Label>{t('patient.travel.arrivalDateRequired')} {t('patient.travel.requiredMark')}</Label>
              <DateField
                value={formData.arrivalDate}
                onChange={(iso) => handleChange('arrivalDate', iso)}
                data-testid="travel-arrival-date"
                required
              />
            </div>
            
            <div>
              <Label>{t('patient.travel.arrivalTimeRequired')} {t('patient.travel.requiredMark')}</Label>
              <Input
                type="time"
                value={formData.arrivalTime}
                onChange={(e) => handleChange('arrivalTime', e.target.value)}
                required
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="needsPickup"
                  checked={formData.needsAirportPickup}
                  onCheckedChange={(checked) => handleChange('needsAirportPickup', checked)}
                />
                <label htmlFor="needsPickup" className="text-sm font-medium cursor-pointer">
                  {t('patient.travel.needPickup')}
                </label>
              </div>
              {formData.needsAirportPickup && (
                <div className="mt-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-700">
                    <Car className="h-5 w-5" />
                    <span className="font-medium">{t('patient.travel.pickupWillBeArranged')}</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    A driver will be waiting for you at the arrivals area with a sign bearing your name.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Departure Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="h-5 w-5 text-sky-600 rotate-45" />
              {t('patient.travel.returnSectionTitle')}
            </CardTitle>
            <CardDescription>{t('patient.travel.returnSectionSub')}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>{t('patient.travel.departureAirport')}</Label>
              <Input
                value={formData.departureAirport}
                onChange={(e) => handleChange('departureAirport', e.target.value)}
                placeholder={t('patient.travel.airportPlaceholder')}
              />
            </div>
            
            <div>
              <Label>{t('patient.travel.flightNumber')}</Label>
              <Input
                value={formData.departureFlightNumber}
                onChange={(e) => handleChange('departureFlightNumber', e.target.value)}
                placeholder={t('patient.travel.flightPlaceholder')}
              />
            </div>
            
            <div>
              <Label>{t('patient.travel.departureDate')}</Label>
              <DateField
                value={formData.departureDate}
                onChange={(iso) => handleChange('departureDate', iso)}
                data-testid="travel-departure-date"
              />
            </div>
            
            <div>
              <Label>{t('patient.travel.departureTime')}</Label>
              <Input
                type="time"
                value={formData.departureTime}
                onChange={(e) => handleChange('departureTime', e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="needsDropoff"
                  checked={formData.needsAirportDropoff}
                  onCheckedChange={(checked) => handleChange('needsAirportDropoff', checked)}
                />
                <label htmlFor="needsDropoff" className="text-sm font-medium cursor-pointer">
                  {t('patient.travel.needDropoff')}
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Accommodation */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hotel className="h-5 w-5 text-amber-600" />
              {t('patient.travel.accommodationTitle')}
            </CardTitle>
            <CardDescription>{t('patient.travel.accommodationSub')}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label>{t('patient.travel.hotelName')}</Label>
              <Input
                value={formData.hotelName}
                onChange={(e) => handleChange('hotelName', e.target.value)}
                placeholder={t('patient.travel.hotelPlaceholder')}
              />
            </div>
            
            <div className="md:col-span-2">
              <Label>{t('patient.travel.hotelAddress')}</Label>
              <Input
                value={formData.hotelAddress}
                onChange={(e) => handleChange('hotelAddress', e.target.value)}
                placeholder={t('patient.travel.hotelAddressPlaceholder')}
              />
            </div>
            
            <div>
              <Label>{t('patient.travel.checkInDate')}</Label>
              <DateField
                value={formData.hotelCheckIn}
                onChange={(iso) => handleChange('hotelCheckIn', iso)}
                data-testid="travel-hotel-checkin"
              />
            </div>
            
            <div>
              <Label>{t('patient.travel.checkOutDate')}</Label>
              <DateField
                value={formData.hotelCheckOut}
                onChange={(iso) => handleChange('hotelCheckOut', iso)}
                data-testid="travel-hotel-checkout"
              />
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-red-600" />
              {t('patient.travel.emergencyContactTitle')}
            </CardTitle>
            <CardDescription>{t('patient.travel.emergencyContactSub')}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label>{t('patient.travel.contactFullName')}</Label>
              <Input
                value={formData.emergencyContact.name}
                onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
                placeholder={t('patient.travel.contactNamePlaceholder')}
              />
            </div>
            
            <div>
              <Label>{t('patient.register.phone')}</Label>
              <PhoneInput
                value={formData.emergencyContact.phone}
                onChange={(e164) => handleEmergencyContactChange('phone', e164)}
                testId="travel-emergency-contact-phone"
                placeholder={t('patient.travel.contactPhonePlaceholder')}
              />
            </div>
            
            <div>
              <Label>{t('patient.travel.relationship')}</Label>
              <Input
                value={formData.emergencyContact.relationship}
                onChange={(e) => handleEmergencyContactChange('relationship', e.target.value)}
                placeholder={t('patient.travel.relationshipPlaceholder')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Special Requirements */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('patient.travel.specialReqTitle')}</CardTitle>
            <CardDescription>{t('patient.travel.specialReqSub')}</CardDescription>
          </CardHeader>
          <CardContent>
            <textarea
              className="w-full min-h-[100px] p-3 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.specialRequirements}
              onChange={(e) => handleChange('specialRequirements', e.target.value)}
              placeholder={t('patient.travel.specialReqPlaceholder')}
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex gap-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate(-1)}
            className="flex-1"
          >
            {t('patient.common.cancel')}
          </Button>
          <Button 
            type="submit"
            disabled={isSaving}
            className="flex-1 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                {t('patient.clinicalProfile.saving')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {t('patient.travel.saveTravelDetails')}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TravelDetails;